import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';
import { GITLAB_COM_DEPLOYMENT_TYPE } from '~/javascripts/purchase/shared/constants';

const CURRENCY_CODE = 'USD';
const EEC_TRANSACTION_SUCCESS_EVENT = 'EECtransactionSuccess';

const EMPTY_NAMESPACE_ID_VALUE = 'not available';
const SAAS_VARIANT = 'SaaS';
const SM_VARIANT = 'Self-Managed';
const SAAS_PREMIUM_SKU = '2c92a00d76f0d5060176f2fb0a5029ff';
const SAAS_ULTIMATE_SKU = '2c92a0ff76f0d5250176f2f8c86f305a';
const SM_PREMIUM_SKU = '2c92a01176f0d50a0176f3043c4d4a53';
const SM_ULTIMATE_SKU = '2c92a00c76f0c6c20176f2f9328b33c9';
const GITLAB_CREDITS_MONTHLY_COMMITTED_POOL_PLAN_ID = '8a1286f999f1412f0199f374ef5c4e50';

const PRODUCT_INFO = {
  [SAAS_ULTIMATE_SKU]: { name: 'Ultimate', id: '0001', variant: SAAS_VARIANT },
  [SAAS_PREMIUM_SKU]: { name: 'Premium', id: '0002', variant: SAAS_VARIANT },
  [SM_ULTIMATE_SKU]: { name: 'Ultimate', id: '0003', variant: SM_VARIANT },
  [SM_PREMIUM_SKU]: { name: 'Premium', id: '0004', variant: SM_VARIANT },
  [GITLAB_CREDITS_MONTHLY_COMMITTED_POOL_PLAN_ID]: {
    name: 'GitLab Credits - Monthly Committed Pool',
    id: '0005',
  },
};

const getProductVariant = (deploymentType, fallbackVariant) => {
  if (!deploymentType) return fallbackVariant;
  return deploymentType === GITLAB_COM_DEPLOYMENT_TYPE ? SAAS_VARIANT : SM_VARIANT;
};

const getProductCategory = (isAdditionalPurchase) => {
  return isAdditionalPurchase ? 'Additional' : 'New';
};

const generateProductInfo = ({ sku, quantity, deploymentType, isAdditionalPurchase }) => {
  const product = PRODUCT_INFO[sku];

  if (!product) {
    Sentry.captureException(`Unexpected product SKU provided to generateProductInfo: ${sku}`);
    return null;
  }

  product.variant = getProductVariant(deploymentType, product.variant);
  product.category = getProductCategory(isAdditionalPurchase);

  const productInfo = {
    ...product,
    brand: 'GitLab',
    quantity,
  };

  return productInfo;
};

const isAvailable = () => Boolean(window.dataLayer);

const pushEnhancedEcommerceEvent = (event, args = {}) => {
  if (!isAvailable()) {
    return;
  }

  try {
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object
    window.dataLayer.push({
      event,
      ...args,
    });
  } catch (e) {
    Sentry.captureException(e);
  }
};

export const trackTransaction = (transactionDetails) => {
  if (!isAvailable()) {
    return;
  }

  const transactionId = uuidv4();
  const {
    paymentOption,
    revenue,
    tax,
    planId,
    quantity,
    namespaceId,
    price,
    deploymentType,
    isAdditionalPurchase = false,
  } = transactionDetails;
  const product = generateProductInfo({
    sku: planId,
    quantity,
    deploymentType,
    isAdditionalPurchase,
  });

  if (!product) {
    return;
  }

  const eventData = {
    ecommerce: {
      currencyCode: CURRENCY_CODE,
      purchase: {
        actionField: {
          id: transactionId,
          affiliation: 'GitLab',
          option: paymentOption,
          revenue: revenue.toString(),
          tax: tax.toString(),
        },
        products: [
          {
            ...product,
            price: price?.toString(),
            dimension36: namespaceId?.toString() || EMPTY_NAMESPACE_ID_VALUE,
          },
        ],
      },
    },
  };

  pushEnhancedEcommerceEvent(EEC_TRANSACTION_SUCCESS_EVENT, eventData);
};
